import { zodResolver } from "@hookform/resolvers/zod"
import { Warning } from "@phosphor-icons/react"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState, type JSX } from "react"
import { useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { z } from "zod"

import { Alert, AlertDescription } from "~/components/reusable/Alert"
import { Button } from "~/components/reusable/Button"
import { Checkbox } from "~/components/design-system/components"
import DatePicker from "~/components/reusable/DatePicker"
import {
  DialogBodyV2,
  DialogClose,
  DialogContentV2,
  DialogFooterV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "~/components/reusable/Dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/reusable/Form"
import { Input } from "~/components/reusable/Input"
import { Tooltip } from "~/components/reusable/Tooltip"
import { useNoosSupplyPlanDetailContext } from "~/context/NoosSupplyPlanDetailProvider"
import { useNoosPlanVariantIds } from "./useNoosPlanVariantIds"
import { invalidateSupplyPlanListReads, parseSettings, useCreateBuy } from "~/hooks/useSupplyPlan"
import { trackEvent } from "~/hooks/useTrackingService"
import { usePrimaryWarehouses } from "~/hooks/useWarehouses"
import { api } from "~/utils/api"
import { startOfTodayUTC } from "~/utils/datetime"
import { showErrorToast } from "~/utils/toaster"
import { NO_VENDOR, resolveVariantVendorId } from "~/utils/vendorUtils"
import InfoTooltip from "../reusable/InfoTooltip"
import { invalidateBuyWrites, toBuyBatchesInput, useNoosBuyPoWriter } from "./useNoosBuyPoWriter"
import {
  getCreateBuyDefaults,
  initialDeselectedWarehouseIds,
  isScopedBuy,
} from "./createBuyDefaults"
import { useNoosBuySuggestion } from "./useNoosBuySuggestion"

const formSchema = z.object({
  name: z.string().min(1, { message: "Required" }),
  buyDate: z.date({ required_error: "Required" }),
})

type FormValues = z.infer<typeof formSchema>

type Props = {
  supplyPlanId: number
  trigger?: JSX.Element
  open?: boolean
  onOpenChange?: (open: boolean) => void
  scopedVariantIds?: string[]
  // The Triggers warehouse filter: the dialog opens with only that warehouse ticked.
  defaultWarehouseId?: string
  onCreated?: () => void
}

export function CreateBuyDialog({
  supplyPlanId,
  trigger,
  scopedVariantIds,
  defaultWarehouseId,
  onCreated,
  open: controlledOpen,
  onOpenChange,
}: Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = (nextOpen: boolean) => {
    setUncontrolledOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }
  const [isSubmitting, setIsSubmitting] = useState(false)
  // Track opt-outs rather than the selection so warehouses loading late start out selected.
  const [deselectedWarehouseIds, setDeselectedWarehouseIds] = useState<Set<string>>(new Set())
  const { supplyPlan } = useNoosSupplyPlanDetailContext()
  const navigate = useNavigate()
  const createBuy = useCreateBuy()
  const utils = api.useUtils()

  const warehouses = usePrimaryWarehouses()
  const selectedWarehouseIds = useMemo(
    () => new Set(warehouses.map((w) => w.id).filter((id) => !deselectedWarehouseIds.has(id))),
    [warehouses, deselectedWarehouseIds],
  )

  const { variantIds: planVariantIds } = useNoosPlanVariantIds()

  const isScoped = isScopedBuy(scopedVariantIds)

  const variantIds = useMemo(
    () => (isScoped ? [...new Set(scopedVariantIds)] : planVariantIds),
    [isScoped, scopedVariantIds, planVariantIds],
  )

  const { variants, shippingLeadTimesByVendor, buildBuyPoBatches } = useNoosBuyPoWriter(variantIds)

  const unconfiguredVendorNames = useMemo(() => {
    if (warehouses.length === 0) return []
    const seen = new Map<string, string>()
    for (const variant of variants) {
      const vendorId = resolveVariantVendorId(variant)
      if (vendorId === NO_VENDOR || seen.has(vendorId)) continue
      const shippingLeadTimes = shippingLeadTimesByVendor.get(vendorId)
      const isMissing = warehouses.some((w) => shippingLeadTimes?.[w.id] === undefined)
      if (isMissing) {
        const vendorName = variant.vendors.find((v) => v.id === vendorId)?.name ?? vendorId
        seen.set(vendorId, vendorName)
      }
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b))
  }, [variants, warehouses, shippingLeadTimesByVendor])

  const getDefaultValues = (): FormValues => getCreateBuyDefaults(isScoped, startOfTodayUTC())
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  })

  const buyDateWatch = form.watch("buyDate")

  const supplyPlanSettings = parseSettings(supplyPlan.settings)

  // The backend computes arrival = orderDate + leadTime per variant, so this buy's coverage window
  // is `[buyDate + leadTime, coverUntil + leadTime]`. Both the quantities previewed here and the
  // window persisted on the POs below come from the same rule (SLIM-576).
  const buyDate = buyDateWatch ?? startOfTodayUTC()
  const {
    coverUntil,
    query: { data: suggestedOrderData, isFetching: isFetchingSuggestedOrders },
  } = useNoosBuySuggestion({ variantIds, buyDate, isScoped, enabled: open })

  // Reset on every open, from the trigger or a controlling parent.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setDeselectedWarehouseIds(
        initialDeselectedWarehouseIds(
          warehouses.map((w) => w.id),
          defaultWarehouseId,
        ),
      )
    }
  }
  // Defaults are read at open time (today's date), so `getDefaultValues` stays out of the deps.
  useEffect(() => {
    if (open) form.reset(getDefaultValues())
  }, [open, form])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) {
      trackEvent("Supply Plan Purchase Order Dialog Opened", {
        "Has Vendor Warning": unconfiguredVendorNames.length > 0,
        "Missing Vendors Count": unconfiguredVendorNames.length,
      })
    }
  }

  const onSubmit = async (values: FormValues) => {
    const suggestedOrders = suggestedOrderData?.suggestedOrders
    if (!suggestedOrders) {
      toast.error("Suggested orders are still loading. Try again in a moment.")
      return
    }

    if (variantIds.length === 0) {
      toast.error("Supply plan has no variants to create a buy from.")
      return
    }

    setIsSubmitting(true)
    try {
      const batches = buildBuyPoBatches({
        buyDate: values.buyDate,
        coverUntil,
        suggestedOrders,
        warehouseIds: selectedWarehouseIds,
      })
      const purchasePlan = await createBuy.mutateAsync({
        supplyPlanId,
        name: values.name,
        buyDate: values.buyDate,
        variantIds: isScoped ? variantIds : undefined,
        batches: toBuyBatchesInput(batches, values.buyDate),
      })
      const purchaseOrderCount = batches.reduce((acc, b) => acc + b.groups.length, 0)

      await invalidateBuyWrites(utils, { supplyPlanId })
      void invalidateSupplyPlanListReads(utils)

      trackEvent("NOOS Supply Plan Buy Created", {
        "Number Of Purchase Orders": purchaseOrderCount,
        "Selected Variants Only": isScoped,
        "Number Of Variants": variantIds.length,
        "Safety Stock Regions": warehouses
          .filter(
            (w) =>
              selectedWarehouseIds.has(w.id) &&
              !supplyPlanSettings.noSafetyStockWarehouseIds.includes(w.id),
          )
          .map((w) => w.name),
        // Carried over from the dialog so the funnel can tell whether users fixed the
        // flagged vendors before buying or pushed through the warning.
        "Has Vendor Warning": unconfiguredVendorNames.length > 0,
        "Missing Vendors Count": unconfiguredVendorNames.length,
      })
      toast.success("Buy created.")
      // Open the newly created buy plan directly instead of returning to the list (SLIM-456).
      await navigate({
        to: "/procurement/supply-plan/noos/$supplyPlanId/buys/$buyId/quantities",
        params: { supplyPlanId: String(supplyPlanId), buyId: String(purchasePlan.id) },
      })
      // After navigation: clearing selection earlier would unmount the command bar
      // (and this dialog) mid-submit.
      onCreated?.()
      setOpen(false)
      form.reset(getDefaultValues())
    } catch (err) {
      showErrorToast(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <DialogRoot open={open} onOpenChange={handleOpenChange}>
      {trigger && (
        <DialogTrigger requires="edit" asChild>
          {trigger}
        </DialogTrigger>
      )}
      <DialogContentV2 className="max-w-[480px]">
        <DialogHeaderV2>
          <DialogTitle>Create buy</DialogTitle>
        </DialogHeaderV2>

        <DialogBodyV2 className="py-5 gap-5">
          {isScoped && (
            <p className="paragraph-sm text-fg-subtle">
              Scoped to {variantIds.length} selected{" "}
              {variantIds.length === 1 ? "variant" : "variants"}. Other variants in the supply plan
              are not included.
            </p>
          )}

          {unconfiguredVendorNames.length > 0 && (
            // Single line: centre the icon on the text instead of Alert's absolute top-4 offset.
            <Alert
              variant="warning"
              className="flex items-center gap-3 [&>svg]:static [&>svg]:shrink-0 [&>svg+div]:translate-y-0 [&>svg~*]:pl-0"
            >
              <Warning className="h-4 w-4" />
              <AlertDescription>
                <Tooltip
                  body={
                    <div className="flex flex-col gap-0.5">
                      {unconfiguredVendorNames.map((name) => (
                        <span key={name}>{name}</span>
                      ))}
                    </div>
                  }
                >
                  <span className="underline decoration-dotted underline-offset-2">
                    {unconfiguredVendorNames.length}{" "}
                    {unconfiguredVendorNames.length === 1 ? "vendor" : "vendors"}
                  </span>
                </Tooltip>{" "}
                {unconfiguredVendorNames.length === 1 ? "is" : "are"} missing shipping lead time
                configuration
              </AlertDescription>
            </Alert>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-[20px]">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-[8px] w-full">
                    <FormLabel className="label-base">Buy name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="---" containerClassName="w-full" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="buyDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-[8px] w-full">
                    <FormLabel className="label-base">Buy Date</FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={(date) => date && field.onChange(date)}
                        minDate={new Date()}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {warehouses.length > 1 && (
                <FormItem className="flex flex-col gap-[8px] w-full">
                  <div className="flex flex-row items-center gap-2">
                    <FormLabel className="label-base">Warehouses</FormLabel>
                    <InfoTooltip body="Decide if this buy considers all your warehouses or only selected warehouse(s)" />
                  </div>
                  <div className="flex flex-col gap-[8px]">
                    {warehouses.map((warehouse) => (
                      <label
                        key={warehouse.id}
                        className="flex items-center gap-2 cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={selectedWarehouseIds.has(warehouse.id)}
                          onCheckedChange={(checked) => {
                            setDeselectedWarehouseIds((prev) => {
                              const next = new Set(prev)
                              if (checked) next.delete(warehouse.id)
                              else next.add(warehouse.id)
                              return next
                            })
                          }}
                        />
                        {warehouse.name}
                      </label>
                    ))}
                  </div>
                </FormItem>
              )}
            </form>
          </Form>
        </DialogBodyV2>

        <DialogFooterV2 className="justify-end gap-3">
          <DialogClose>
            <Button asChild variant="neutral">
              Cancel
            </Button>
          </DialogClose>
          <Button
            requires="edit"
            allowedTitle={
              isFetchingSuggestedOrders
                ? "Recalculating suggested orders"
                : warehouses.length > 1 && selectedWarehouseIds.size === 0
                  ? "Select at least 1 warehouse"
                  : undefined
            }
            onClick={(e) => {
              if (isFetchingSuggestedOrders) {
                e.preventDefault()
                return
              }
              form.handleSubmit(onSubmit)()
            }}
            loading={isSubmitting || isFetchingSuggestedOrders}
            disabled={warehouses.length > 1 && selectedWarehouseIds.size === 0}
          >
            Create buy
          </Button>
        </DialogFooterV2>
      </DialogContentV2>
    </DialogRoot>
  )
}
